import { Link } from 'react-router-dom';

export interface ParticipationRecordData {
  id: string;
  fair: string;
  edition: string;
  dates: string;
  venue: string;
  city: string;
  stand: string | null;
  standNote: string;
  summary: string;
  documentation: string[];
  photos: string[];
  reportLink: string;
}

interface ParticipationRecordProps {
  record: ParticipationRecordData;
  index: number;
  onPhotoClick: (photo: string, caption: string) => void;
}

export default function ParticipationRecord({ record, index, onPhotoClick }: ParticipationRecordProps) {
  const isAlternate = index % 2 === 1;

  return (
    <article className="bg-white border border-cream-200 rounded-2xl overflow-hidden hover:border-gold-300 transition-colors duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Photos */}
        <div className={`p-4 md:p-5 bg-cream-50 ${isAlternate ? 'lg:order-2' : ''}`}>
          <button
            type="button"
            onClick={() => onPhotoClick(record.photos[0], `${record.fair} ${record.edition} — ${record.venue}`)}
            className="block w-full aspect-[16/10] rounded-xl overflow-hidden group cursor-pointer"
            aria-label={`View photographs from ${record.fair} ${record.edition}`}
          >
            <img
              src={record.photos[0]}
              alt={`${record.fair} ${record.edition} booth, ${record.venue}`}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </button>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {record.photos.slice(1, 3).map((photo, idx) => (
              <button
                key={photo}
                type="button"
                onClick={() => onPhotoClick(photo, `${record.fair} ${record.edition} — detail ${idx + 2}`)}
                className="block aspect-[4/3] rounded-lg overflow-hidden group cursor-pointer"
                aria-label={`View photograph ${idx + 2} from ${record.fair} ${record.edition}`}
              >
                <img
                  src={photo}
                  alt={`${record.fair} ${record.edition} detail`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </button>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-navy-500 flex items-center gap-1.5">
            <i className="ri-camera-line text-gold-500"></i>
            Taken on the floor during the fair dates shown. Click any image to enlarge.
          </p>
        </div>

        {/* Details */}
        <div className={`p-6 md:p-8 lg:p-9 flex flex-col justify-center ${isAlternate ? 'lg:order-1' : ''}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-gold-700 bg-gold-50 border border-gold-200 rounded-full whitespace-nowrap">
              {record.edition}
            </span>
            <span className="text-[11px] text-navy-500 uppercase tracking-wider">
              Documented appearance
            </span>
          </div>

          <h3 className="text-xl md:text-2xl font-bold text-navy-900 leading-snug mb-4">
            {record.fair}
          </h3>

          <dl className="space-y-2.5 mb-5">
            <div className="flex items-start gap-3">
              <dt className="w-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-building-2-line text-gold-500 text-sm"></i>
              </dt>
              <dd className="text-sm text-navy-700">
                {record.venue}, {record.city}
              </dd>
            </div>
            <div className="flex items-start gap-3">
              <dt className="w-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-calendar-line text-gold-500 text-sm"></i>
              </dt>
              <dd className="text-sm text-navy-700">{record.dates}</dd>
            </div>
            <div className="flex items-start gap-3">
              <dt className="w-4 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-map-pin-2-line text-gold-500 text-sm"></i>
              </dt>
              <dd className="text-sm">
                {record.stand ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="font-semibold text-navy-900">{record.stand}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gold-100 text-[10px] font-medium text-gold-700 rounded-full whitespace-nowrap">
                      <i className="ri-check-line text-[10px]"></i>
                      Photographed
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-cream-100 text-[11px] font-medium text-navy-600 rounded-full whitespace-nowrap">
                    <i className="ri-file-text-line"></i>
                    Stand number on request
                  </span>
                )}
              </dd>
            </div>
          </dl>

          <p className="text-sm text-navy-600 leading-relaxed mb-5">{record.summary}</p>

          <div className="bg-cream-50 border border-cream-200 rounded-xl p-4 mb-5">
            <p className="text-[11px] font-semibold text-navy-900 uppercase tracking-wider mb-3">
              What we can send you
            </p>
            <ul className="space-y-2">
              {record.documentation.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs text-navy-600 leading-relaxed">
                  <span className="w-4 h-4 flex items-center justify-center rounded-full bg-gold-100 text-gold-600 shrink-0 mt-0.5">
                    <i className="ri-check-line text-[10px]"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-navy-500 leading-relaxed mb-5 italic">{record.standNote}</p>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={record.reportLink}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-colors duration-300 whitespace-nowrap"
            >
              {record.reportLink === '/events' ? 'See the full archive' : 'Read the event report'}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/contact-us"
              className="inline-flex items-center gap-2 text-sm font-medium text-navy-600 hover:text-gold-600 transition-colors duration-300 whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              Request the paperwork
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}